import {
    BadgeTemplateClassifierColumns,
    BadgeTemplateClassifierData,
} from '@juvare/badging/backend/config/classifiers/badge-template-classifier/types';
import { EntityWithData } from '@juvare/badging/backend/types/EntityWithIdAndData';
import { BadgeCreateHeaderInfoForm } from '@juvare/badging/ui/features/badge-management/components/BadgeCreateWIzard/BadgeCreateHeaderInfoForm';
import { BadgeCreateWizardStepContainer } from '@juvare/badging/ui/features/badge-management/components/BadgeCreateWIzard/BadgeCreateWizardStepContainer';
import { BadgeTemplatesSelect } from '@juvare/badging/ui/features/badge-management/components/BadgeTemplatesPreviewSelect';
import { BadgeDetailsFormConfigData } from '@juvare/badging/ui/features/badge-management/utils/getBadgeDetailsFormConfig';
import { FormRef } from '@juvare/common-form-ui/Form';
import { WizardStepComponentProps, WizardStepComponentRef } from '@juvare/common-form-ui/Wizard';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { GetResponse } from '@juvare/platform-shared/features/storage';
import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCreateWizardData, BadgeCreateWizardStep1Data } from './types';

export const RmsBadgeCreateWizardStep1 = React.forwardRef<WizardStepComponentRef, WizardStepComponentProps>((props, ref) => {
    const { t } = useTranslation('rms');

    const data = (props.data ?? {}) as BadgeCreateWizardData;
    const badgeDocumentName = data.badgeDocumentName;
    const badgeDescription = data.badgeDescription ?? '';

    const [formData, setFormData] = useState<BadgeDetailsFormConfigData>({ badgeDocumentName, badgeDescription });

    const refBadgeCreateHeaderInfoForm = React.useRef<FormRef<BadgeDetailsFormConfigData>>(null);

    const [badgeTemplate, setBadgeTemplate] = React.useState<EntityWithData<BadgeTemplateClassifierData> | undefined>(data.badgeTemplate);
    const handleSelectBadgeTemplate = (
        badgeTemplate: GetResponse<BadgeTemplateClassifierData, BadgeTemplateClassifierColumns> | undefined,
    ) => {
        setBadgeTemplate(badgeTemplate);
    };

    React.useImperativeHandle(ref, () => ({
        getData: (): BadgeCreateWizardStep1Data => {
            return {
                badgeTemplate,
                badgeDocumentName,
                badgeDescription,
            };
        },
        submit: async () => {
            const dataNew: BadgeCreateWizardStep1Data = {
                ...formData,
                badgeTemplate,
            };

            try {
                // force form validation
                await refBadgeCreateHeaderInfoForm.current?.submit();
            } catch {
                return;
            }

            if (!dataNew.badgeDocumentName || !dataNew.badgeDocumentName.trim()) {
                return; // do not reject, avoid error dialog
            }
            if (!dataNew.badgeTemplate) {
                return Promise.reject(new Error(t('badging.create.wizard.step1.errorSelectBadgeTemplate')));
            }

            return Promise.resolve(
                props.onSubmit(dataNew).then((response) => {
                    props.onSubmitSuccess(dataNew, response);
                }),
            );
        },
    }));

    return (
        <BadgeCreateWizardStepContainer
            headerText={t('badging.create.wizard.step1.contentTitle')}
            badgeTemplateSelected={badgeTemplate}
            fixedBadgePreviewPosition={true}
        >
            <Box
                sx={{
                    p: 2,
                    width: '100%',
                    backgroundColor: themeColors.surface.secondary,
                }}
            >
                <Typography
                    variant='subtitle1'
                    sx={{
                        mb: 2,
                    }}
                >
                    {t('badging.create.wizard.step1.badgeInformation')}
                </Typography>

                <BadgeCreateHeaderInfoForm formRef={refBadgeCreateHeaderInfoForm} state={formData} setState={setFormData} />
            </Box>
            <Box
                sx={{
                    p: 2,
                    backgroundColor: themeColors.surface.secondary,
                }}
            >
                <Typography
                    variant='subtitle1'
                    sx={{
                        mb: 2,
                    }}
                >
                    {t('badging.create.wizard.step1.chooseTemplate')}
                </Typography>
                <BadgeTemplatesSelect badgeTemplateSelected={badgeTemplate} onSelectBadgeTemplate={handleSelectBadgeTemplate} />
            </Box>
        </BadgeCreateWizardStepContainer>
    );
});
